<template lang="pug">
  #member-account-settings
    MemberSettingsFormWrapper(
      :form-data="formData"
      :disable-save.sync="disableSave"
      :keys-to-reset="keysToReset"
    )
      ContentLoader(:width="300" :height="100" v-if="!memberLoaded")
        rect(x="0", y="0", rx="4", ry="4", width="30%", height="80")
        rect(x="33%", y="0", rx="4", ry="4", width="60%", height="15")
        rect(x="33%", y="20", rx="4", ry="4", width="60%", height="15")
        rect(x="33%", y="40", rx="4", ry="4", width="60%", height="15")
        rect(x="33%", y="60", rx="4", ry="4", width="60%", height="15")
        rect(x="33%", y="80", rx="4", ry="4", width="60%", height="15")

      .columns(v-else)
        .column.is-one-third
          .card.your-data-card
            .card-content
              WtTitle(card-title) Your Data
              .level.is-size-5 Registered on: {{ this.moment.unix(member.registeredDate).format("MMM DD, YYYY") }}
              .level.is-size-5 Total Step Entries: {{ _.get(member, "memberData.numberOfEntries", "") }}
              WtButton.level(
                @click.native="downloadData"
                :loading="processingDownload"
              ) Download Data
              .level.horizontal-divider
              WtTitle.level(card-title) Delete Your Account
              .level.is-size-5.has-text-danger.is-italic Warning: This is not reversible
              WtButton.level(
                color="red"
                @click.native="deleteAccount"
                ) Delete Account
        .column.is-two-thirds.pr-3.pl-3
          WtTitle(card-title) Privacy & Communications
          .columns.is-multiline
            WtSwitchQuestionInput.column.is-half(
              v-if="!isCoreTier"
              label="Allow other members to view your friends?"
              true-value="Yes"
              false-value="No"
              v-model="friendsViewable"
              @input="disableSave = isOriginalMemberDataValue('friendsViewable', friendsViewable)"
            )
            WtSwitchQuestionInput.column.is-half(
              v-if="!isCoreTier"
              label="Show my activity to my friends in the newsfeed?"
              v-model="showMyActivity"
              @input="disableSave = isOriginalMemberDataValue('showMyActivity', showMyActivity)"
            )
            .block.column.is-half
              WtRadioQuestionInput(
                label="Allow other members to comment on your public posts?"
                label-tooltip="You can still delete any comments on your journal."
              )
                template(v-slot:radioOne)
                  BRadio(
                    v-model="allowComments"
                    native-value="Yes"
                    @input="disableSave = isOriginalMemberDataValue('allowComments', allowComments)"
                  ) Yes
                template(v-slot:radioTwo)
                  BRadio(
                    v-model="allowComments"
                    native-value="Com"
                    @input="disableSave = isOriginalMemberDataValue('allowComments', allowComments)"
                  ) My friends only
                template(v-slot:radioThree)
                  BRadio(
                    v-model="allowComments"
                    native-value="No"
                    @input="disableSave = isOriginalMemberDataValue('allowComments', allowComments)"
                  ) No
            .block.column.is-half
              WtRadioQuestionInput(
                label="Receive new comment notifications via:"
                :label-tooltip="commentNotificationsTooltip"
              )
                template(v-slot:radioOne)
                  BRadio(
                    v-model="notificationPreference"
                    native-value="email"
                    @input="disableSave = isOriginalMemberDataValue('notificationPreference', notificationPreference)"
                  ) Email & website
                template(v-slot:radioTwo)
                  BRadio(
                    v-model="notificationPreference"
                    native-value="website"
                    @input="disableSave = isOriginalMemberDataValue('notificationPreference', notificationPreference)"
                  ) Website only
          .horizontal-divider
          WtTitle(card-title) Step Settings
          .block
            WtRadioQuestionInput(
              label="Measurement Units"
              :label-tooltip="unitsTooltip"
            )
              template(v-slot:radioOne)
                BRadio.define-radio(
                    v-model="units"
                    native-value="Miles"
                    @input="disableSave = isOriginalMemberDataValue('units', units)"
                  )
                    | Miles/Pounds
              template(v-slot:radioTwo)
                BRadio.define-radio(
                    v-model="units"
                    native-value="Kilometers"
                    @input="disableSave = isOriginalMemberDataValue('units', units)"
                  )
                    | Kilometers/Kilograms
          .horizontal-divider
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import WtTitle from "@@/WtTitle/WtTitle";
import WtTile from "@@/WtTile/WtTile";
import { ContentLoader } from "vue-content-loader";
import memberSettingsFormMixin from "@/mixins/memberSettingsFormMixin";
import analyticsTrackingMixin from "@/mixins/analyticsTrackingMixin";
import MemberSettingsFormWrapper from "@@/MemberSettings/MemberSettingsFormWrapper";
import WtButton from "@@/WtButton/WtButton";
import WtSwitchQuestionInput from "@@/WtSwitchQuestionInput/WtSwitchQuestionInput";
import WtRadioQuestionInput from "@@/WtRadioQuestionInput/WtRadioQuestionInput";
import WtLabel from "@@/WtLabel/WtLabel";

export default {
  name: "MemberAccount",

  components: {
    WtLabel,
    WtButton,
    WtTitle,
    WtTile,
    ContentLoader,
    MemberSettingsFormWrapper,
    WtSwitchQuestionInput,
    WtRadioQuestionInput
  },

  mixins: [analyticsTrackingMixin, memberSettingsFormMixin],

  data() {
    return {
      keysToReset: [
        {
          key: "memberData",
          subKey: "friendsViewable"
        },
        {
          key: "memberData",
          subKey: "showMyActivity"
        },
        {
          key: "memberData",
          subKey: "allowComments"
        },
        {
          key: "memberData",
          subKey: "notificationPreference"
        },
        {
          key: "memberData",
          subKey: "units"
        }
      ],
      processingDownload: false
    };
  },

  computed: {
    ...mapGetters("members", ["member", "memberCopy", "memberLoaded"]),
    ...mapGetters("program", ["isCoreTier"]),

    formData() {
      return {
        friendsViewable: this.friendsViewable,
        showMyActivity: this.showMyActivity,
        allowComments: this.allowComments,
        notificationPreference: this.notificationPreference,
        units: this.units
      };
    },

    friendsViewable: {
      set(friendsViewable) {
        this.updateMemberCopy({
          key: "memberData",
          subKey: "friendsViewable",
          value: friendsViewable
        });
      },
      get() {
        return this._.get(this.memberCopy, "memberData.friendsViewable", "");
      }
    },

    showMyActivity: {
      set(showMyActivity) {
        this.updateMemberCopy({
          key: "memberData",
          subKey: "showMyActivity",
          value: showMyActivity
        });
      },
      get() {
        return this._.get(this.memberCopy, "memberData.showMyActivity", "");
      }
    },

    allowComments: {
      set(allowComments) {
        this.updateMemberCopy({
          key: "memberData",
          subKey: "allowComments",
          value: allowComments
        });
      },
      get() {
        return this._.get(this.memberCopy, "memberData.allowComments", "");
      }
    },

    notificationPreference: {
      set(notificationPreference) {
        this.updateMemberCopy({
          key: "memberData",
          subKey: "notificationPreference",
          value: notificationPreference
        });
      },
      get() {
        return this._.get(
          this.memberCopy,
          "memberData.notificationPreference",
          ""
        );
      }
    },

    units: {
      set(units) {
        this.updateMemberCopy({
          key: "memberData",
          subKey: "units",
          value: units
        });
      },
      get() {
        return this._.get(this.memberCopy, "memberData.units", "");
      }
    },

    commentNotificationsTooltip() {
      return `How you will be notified of comments on your journal entries.`;
    },

    unitsTooltip() {
      return `
        Select unit of measurement (miles or kilometers).
      `;
    }
  },

  mounted() {
    this.trackFeatureView("settings", "settings_account");
  },

  methods: {
    ...mapActions("members", ["updateMemberCopy", "delete", "accountDownload"]),
    ...mapActions(["snackBar", "resetState"]),

    async deleteAccount() {
      this.$buefy.dialog.confirm({
        title: "Deleting account",
        confirmText: "Delete Account",
        type: "is-danger",
        hasIcon: true,
        message:
          "Are you sure you want to delete your account? This cannot be undone.",
        onConfirm: () => {
          this.delete(this.$auth.user().id).then(response => {
            if (response && response.data.success) {
              this.$auth.logout();
              this.resetState();
              window.location.reload();
            }
            if (response.data.error) {
              this.snackBar({
                duration: 10000,
                type: "is-danger",
                message: response.data.error.message
              });
            }
          });
        }
      });
    },

    downloadData() {
      this.processingDownload = true;

      this.accountDownload(this.$auth.user().id).then(response => {
        if (response.status === 200) {
          this.snackBar({
            duration: 5000,
            type: "is-success",
            message:
              "Your report will be emailed to you once we've finished processing it."
          });
        } else {
          this.snackBar({
            duration: 10000,
            type: "is-danger",
            message: response.data.error.message
          });
        }

        this.processingDownload = false;
      });
    }
  }
};
</script>

<style lang="sass" scoped>
.your-data-card
  box-shadow: 0 2px 3px rgba($black, 0.1), 0 0 0 1px rgba($black, 0.1) // bulma's old card shadow
</style>
